'use strict';

define('vb/action/actions',['vb/private/action/actionRunner', 'vb/private/utils'], (ActionRunner, Utils) => {
  /** @typedef {import('vb/contexts').ContainerContext} ContainerContext */
  /** @typedef {import('vb/action/action').Outcome} Outcome */
  /** @template K @typedef {import("ojs/ojdataprovider").ItemMetadata<K>} ItemMetadata */
  /** @template K,D @typedef {import('ojs/ojdataprovider').DataProvider<K, D>} DataProvider<K, D> */

  /**
   * @typedef {'aztec'|'code_128'|'code_39'|'code_93'|'codabar'|'data_matrix'|'ean_13'|'ean_8'|'itf'|'pdf417'|'qr_code'
   * |'upc_a'|'upc_e'} BarcodeFormat
   * @see [BarcodeFormat]{@link https://wicg.github.io/shape-detection-api/#enumdef-barcodeformat}
   */

  /**
   * @typedef {Object} DetectedBarcode
   * @property {string} rawValue - String decoded from the barcode.
   * @property {BarcodeFormat} format - Detected BarcodeFormat.
   * @see [DetectedBarcode]{@link https://wicg.github.io/shape-detection-api/#detectedbarcode}
   */

  /**
   * API for invoking various actions in a code-based action chain.
   * @category Action
   */
  class Actions {
    /**
     * This action method assigns a value to a VB variable. The following is an example of a parameters
     * object:
     *
     * {
     *   variable: '$page.variables.pageVar',
     *   value: 'some value',
     *   auto: 'ifNoMapping',
     *   reset: 'toDefault',
     *   mapping: {...}
     * }
     *
     * @param {ContainerContext} context - the context object for the action chain
     * @param {Object} parameters
     * @param {String} parameters.variable - the fully qualified variable name, e.g., $page.variables.pageVar
     * @param {*} parameters.value - the value to assign
     * @param {String} [parameters.auto] - controls whether to auto-assign all properties from the given value to
     * the corresponding properties on the variable. If auto is set to 'ifNoMapping', auto-assignment will
     * only be performed if no mapping is provided. If auto is set to 'always', the default, auto-assignment
     * will always be performed first before any mapping is applied.
     * @param {String} [parameters.reset] - can be either be 'none' (the default), 'toDefault', or 'empty'. The
     * toDefault' option means that the action will first reset the variable to its default value before
     * assignment. The 'none' option means to override the existing value. The 'empty' option means empty
     * out the variable before assignment.
     * @param {Object} [parameters.mapping] - metadata used to provide fine-grained control over what gets assigned
     * from the source to the target.
     * @param {Object} [options]
     * @param {String} [options.id] - an optional identifier for the action
     * @returns {Promise<void>} returns a promise that resolves to undefined if the assignment is successful
     * @throws {Error} throws an error if the assignment fails
     * @deprecated replaced with direct assignment
     */
    static async assignVariable(context, parameters, options) {
      const clonedParams = Utils.cloneObject(parameters);

      // default auto to always
      clonedParams.auto = clonedParams.auto || 'always';

      /// default reset to none
      clonedParams.reset = clonedParams.reset || 'none';

      const opts = Object.assign({}, options, { alias: 'Actions.assignVariable' });
      return ActionRunner.run('vb/action/builtin/assignVariablesAction',
        context, clonedParams, opts);
    }

    /**
     * This action method enables web applications to decode information from QR codes and barcodes such as urls,
     * wi-fi connections, and contact information. The following is an example of a parameters object:
     *
     * {
     *   image: document.querySelector('#qrcode'),
     *   formats: [ 'qr_code' ],
     *   convertBlob: true,
     * }
     *
     * @param {ContainerContext} context - the context object for the action chain
     * @param {Object} parameters - parameters for the action
     * @param {CanvasImageSource|Blob|ImageData|HTMLImageElement} parameters.image - The image to detect barcodes in.
     * @param {BarcodeFormat[]} [parameters.formats] - A series of barcode formats to search for.
     * Not all formats may be supported on all platforms.
     * If formats is not specified, the browser will search all supported formats, so limiting the search to
     * a particular subset of supported formats is likely to provide better performance.
     * @param {boolean} [parameters.convertBlob] - Indicates whether an automatic conversion from Blob input to
     * ImageBitmap should be performed. This is a workaround for Chrome's bug:
     * {@link https://bugs.chromium.org/p/chromium/issues/detail?id=1048379}
     * If conversion fails, an error will be thrown. This will happen, for example, when Blob does not contain
     * a valid image source.
     * @see [BarcodeDetectorOptions]{@link https://wicg.github.io/shape-detection-api/#dictdef-barcodedetectoroptions}
     * @param {Object} [options]
     * @param {string} [options.id] - an identifier for the action
     * @returns {Promise<DetectedBarcode>} on success, returns a DetectedBarcode object as a result.
     * DetectedBarcode has a rawValue property that corresponds to the decoded string.
     * In the case where a barcode is not detected, an empty object is returned.
     *
     * @throws {TypeError} If the image parameter is missing or invalid.
     * @throws {Error} If the browser does not support Shape Detection API, or a specified format is not supported.
     */
    static async barcode(context, parameters, options) {
      const opts = Object.assign({}, options, { alias: 'Actions.barcode' });
      return ActionRunner.run('vb/action/builtin/barcodeAction',
        context, parameters, opts);
    }

    /**
     * This action method invokes another action chain. The following is an example of a parameters object:
     *
     * {
     *   chain: 'myActionChain',
     *   params: {
     *     p1: 'some value'
     *   }
     * }
     *
     * On success, the result of the called action chain will be returned. Any error thrown from the called
     * action chain will be propagated to the calling chain.
     *
     * @async
     * @param {ContainerContext} context - the context object for the action chain.
     * @param {Object} parameters
     * @param {String} parameters.chain - the chain id to invoke
     * @param {Object} [parameters.params] - parameters to the chain (optional)
     * @param {Object} [options]
     * @param {String} [options.id] - an optional identifier for the action
     * @returns {Promise<*>} returns a promise that resolves to the result of the called action chain.
     * @throws {Error} any error thrown from the called action chain will be propagated to the calling chain.
     */
    static async callChain(context, parameters, options) {
      const opts = Object.assign({}, options, { alias: 'Actions.callChain' });
      return ActionRunner.run('vb/action/builtin/callChainAction',
        context, parameters, opts);
    }

    /**
     * This action method invokes a method on a component identified by a CSS selector or a component element.
     * The following is an example of a parameters object:
     *
     * {
     *   selector: '#vbDefaultNotifier',
     *   method: 'show',
     *   params: [p1, p2],
     * }
     *
     * @param {ContainerContext} context - The context object for the action chain.
     * @param {Object} parameters - Parameters for the action.
     * @param {Element} [parameters.component] - The component element. If provided, it will be used directly.
     * @param {String} [parameters.selector] - A CSS selector used to locate the component. If provided, it will
     * override the parameters.component property.
     * @param {String} parameters.method - The method to invoke on the component.
     * @param {Array} [parameters.params] - A list of parameters to the method.
     * @param {Object} [options] - Options for the action.
     * @param {String} [options.id] - An optional identifier for the action.
     * @returns {Promise<*>} A promise that resolves to the result of the called component method if successful.
     * @throws {Error} If an error occurs when invoking the component method.
     */
    static async callComponentMethod(context, parameters, options) {
      const opts = Object.assign({}, options, { alias: 'Actions.callComponentMethod' });
      return ActionRunner.run('vb/action/builtin/callComponentMethodAction',
        context, parameters, opts);
    }

    /**
     * This action method fires a custom event. The following is an example of a parameters object:
     *
     * {
     *   name: "myCustomEvent",
     *   payload: {...},
     * }
     *
     * On success, the method returns after all the relevant event listeners are invoked depending on the
     * propagation behaviors. It will throw an error, otherwise.
     *
     * @param {ContainerContext} context - The context object for the action chain.
     * @param {Object} parameters - The parameters object containing the event details.
     * @param {String} [parameters.name] - The name of the event to fire.
     * @param {String} [parameters.event] - The name of the event to fire. This is used by the Actions api.
     * @param {Object} [parameters.payload] - The payload associated with the event.
     * @param {String} [parameters.target] - The target of the event action.
     * @param {Object} [options] - Options for the action.
     * @param {String} [options.id] an optional identifier for the action.
     * @returns {Promise<undefined|Object>} a promise that resolves when the event is successfully delivered.
     * The resolved value is either `undefined` or an object containing the result of the execution,
     * depending on the event behavior type.
     * @throws {Error} if the event cannot be fired.
     */
    static async fireEvent(context, parameters, options) {
      const opts = Object.assign({}, options, { alias: 'Actions.fireEvent' });
      return ActionRunner.run('vb/action/builtin/fireCustomEventAction',
        context, parameters, opts);
    }

    /**
     * Fires an AI response event.
     *
     * @param {ContainerContext} context - The context object for the action chain.
     * @param {Object} parameters - Parameters for the action.
     * @param {string} parameters.event - The name of the event to fire. This is used by the Actions api.
     * @param {Object} parameters.payload - The payload associated with the event.
     * @param {string} [parameters.target] - The target of the event action. If target is not provided,
     * the event will be broadcast to all plugins that have listeners defined for the event.
     * @param {Object} [options] - Options for the action.
     * @param {String} [options.id] - An optional identifier for the action.
     * @returns {Promise<*>} Returns a promise that resolves when the event is successfully fired.
     */
    static async fireAiResponseEvent(context, parameters, options) {
      const opts = Object.assign({}, options, { alias: 'Actions.fireAiResponseEvent' });
      return ActionRunner.run('vb/action/builtin/fireAiResponseEventAction',
        context, parameters, opts);
    }

    /**
     * Fires an AI request event.
     *
     * @param {ContainerContext} context - The context object for the action chain.
     * @param {Object} parameters - Parameters for the action.
     * @param {Object} parameters.payload - The payload associated with the event.
     * @param {string} [parameters.event] - The name of the event to fire. Defaults to 'default'.
     * @param {Object} [options] - Options for the action.
     * @param {String} [options.id] - An optional identifier for the action.
     * @returns {Promise<*>} Returns a promise that resolves when the event is successfully fired.
     */
    static async fireAiRequestEvent(context, parameters, options) {
      const opts = Object.assign({}, options, { alias: 'Actions.fireAiRequestEvent' });
      return ActionRunner.run('vb/action/builtin/fireAiRequestEventAction',
        context, parameters, opts);
    }

    /**
     * Dispatches an event to the 'target' data provider (of type oj.DataProvider) to notify its listeners
     * of changes to the underlying data / state.
     *
     * This method expects either mutation operation(s) or a refresh operation as part of the input parameters, but
     * not both.
     *
     * A refresh operation is used when the target data provider needs to be refreshed (possibly forcing a re-fetch of
     * data). A null value is generally specified for the 'refresh' param as it does not support a payload.
     *
     * A mutation operation can include one or more operation types like 'add', 'update', 'remove', as long as the
     * key...values between operations do not intersect. For example, you cannot add a record and remove it in the
     * same event.
     *
     * Note: Sub properties on specific operation types are generally based on JET DataProviderOperationEventDetail,
     * DataProviderAddOperationEventDetail and DataProviderRefreshEventDetail. Not all properties on the JET API
     * are supported on this action. Additionally while all sub properties are marked optional, when no value is
     * provided for add/update/remove payload, the action throws a failure outcome.
     *
     * @param {ContainerContext} context the context object for the action chain
     * @param {Object} parameters
     * @param {DataProvider<K, D>} parameters.target the target data provider for the event. Usually a VB data provider
     * @param {Object} [parameters.add] mutation operation for adding data to a data provider containing the following
     * properties that are similar to JET DataProviderAddOperationEventDetail.
     * @param {Set.<K>|Array.<K>} [parameters.add.keys] unique keys of items involved in the add operation, where K is
     * the type of key data
     * @param {Array<K>} [parameters.add.addBeforeKeys] array of keys that the added data should come after.
     * @param {Array<D>} [parameters.add.data] data being added.
     * @param {number[]} [parameters.add.indexes] array of indexes affected by the add operation.
     * @param {Array<ItemMetadata<K>>} [parameters.add.metadata] metadata associated with the mutation operation.
     *
     * @param {Object} [parameters.update] mutation operation for updating data to a data provider containing the
     * following properties that are similar to JET DataProviderOperationEventDetail.
     * @param {Set<K>|Array<K>} [parameters.update.keys] Unique keys of items involved in the mutate operation, where
     * K is the type of key data
     * @param {D[]} [parameters.update.data] data being added.
     * @param {number[]} [parameters.update.indexes] array of indexes affected by the mutate operation.
     * @param {Array<ItemMetadata<K>>} [parameters.update.metadata] metadata associated with the mutation operation.
     *
     *  @param {Object} [parameters.remove] mutation operation for removing data from a data provider containing the
     * following properties that are similar to JET DataProviderOperationEventDetail.
     * @param {Set<K>|Array<K>} [parameters.remove.keys] Unique keys of items involved in the mutate operation, where
     * K is the type of key data
     * @param {Array<D>} [parameters.remove.data] data being added.
     * @param {number[]} [parameters.remove.indexes] array of indexes affected by the mutate operation.
     * @param {Array<ItemMetadata<K>>} [parameters.remove.metadata] metadata associated with the mutation operation.
     *
     *  @param {?{}} [parameters.refresh] refresh event does not require a payload, so set null or an empty object.
     * Sub-properties specified for 'refresh' are not passed-through to the data provider.
     * @param {Object} [options]
     * @param {String} [options.id] an optional identifier for the action
     *
     * @returns {Promise<*>} when event is successfully delivered
     *
     * @throws {Error} If both mutation and refresh events are provided, or neither is provided.
     * @throws {Error} If target is not a valid DataProvider instance.
     *
     * @template K type of key
     * @template D type of data
     *
     * @example
     * // A chain that fires a mutation event and refresh event on an array data provider,
     * // - updates employee identified by the key 'emp1'
     * // - adds a new employee,
     *
     *  const { $page } = context;
     *  const empDP = $page.variables.empADP;
     *
     *  // add a new employee op
     *  const addOp = {
     *    data: [{id: 'emp2', value: 'Employee 2'}],
     *    keys: ['emp2'],
     *  }
     *
     *  // update an existing employee op
     *  const updateKeysSet = new Set(['emp1']);
     *  const updateOp = {
     *    keys: updateKeysSet,
     *    indexes: [1],
     *    data: [{ id: 'emp1', value: 'update Employee 1' }],
     *  };
     *
     *  // fire action to update and add to DataProvider together
     *  await Actions.fireDataProviderEvent(context, {
     *    target: empDP,
     *    add: addOp,
     *    update: updateOp,
     *  });
     *
     *  // fire action to refresh the DataProvider
     *  await Actions.fireDataProviderEvent(context, {
     *    target: empDP,
     *    refresh: null,
     *  });
     *
     */
    static async fireDataProviderEvent(context, parameters, options) {
      const opts = Object.assign({}, options, { alias: 'Actions.fireDataProviderEvent' });
      return ActionRunner.run('vb/action/builtin/fireDataProviderEventAction',
        context, parameters, opts);
    }

    /**
     * This action method fires a notification event. The following is an example of a parameters object:
     *
     * {
     *   summary: "some message",
     *   type: 'error',
     *   displayMode: 'persist"
     * }
     *
     * @param {ContainerContext} context the context object for the action chain.
     * @param {Object} [parameters] - The parameters for the notification event.
     * @param {String} [parameters.summary] - The title or summary of the notification event.
     * @param {String} [parameters.message] - The notification event text.
     * @param {'persist'|'transient'} [parameters.displayMode='persist'] - The display mode of the event.
     * @param {'error'|'warning'|'confirmation'|'info'} [parameters.type='error'] - The type of the event.
     * @param {String} [parameters.key] - A unique key for the notification event.
     * If not provided, a 9 character alphanumeric UID is generated randomly.
     * @param {String} [parameters.target] - The target container for the event. Defaults to 'leaf'.
     * @param {Object} [options] - Options for the action.
     * @param {String} [options.id] - An optional identifier for the action.
     * @returns {Promise<void>} a promise that resolves when the notification event is successfully delivered.
     * @throws {Error} if the event is not successfully delivered.
     */
    static async fireNotificationEvent(context, parameters, options) {
      const clonedParams = Utils.cloneObject(parameters);

      // default target to leaf
      clonedParams.target = clonedParams.target || 'leaf';

      const opts = Object.assign({}, options, { alias: 'Actions.fireNotificationEvent' });
      return ActionRunner.run('vb/action/builtin/fireNotificationEventAction',
        context, clonedParams, opts);
    }

    /* eslint-disable max-len */
    /**
     * The action method provides access to geographical location information associated with the hosting
     * device. This action requires user's consent and, as a best practice, should only be fired on user
     * gesture. Doing so will allow users to more easily associate the system permission prompt for access
     * with the action they just initiated.
     *
     * The parameters object corresponds to
     * [PositionOptions]{@link https://developer.mozilla.org/en-US/docs/Web/API/PositionOptions}:
     *  - [maximumAge]{@link https://developer.mozilla.org/en-US/docs/Web/API/PositionOptions/maximumAge}, default: 0
     *  - [timeout]{@link https://developer.mozilla.org/en-US/docs/Web/API/PositionOptions/timeout}, default: Infinity
     *  - [enableHighAccuracy]{@link https://developer.mozilla.org/en-US/docs/Web/API/PositionOptions/enableHighAccuracy},
     *  default: false
     *
     * On mobile devices, enableHighAccuracy should be set to true in order to use GPS sensors.
     *
     * If geolocation API is supported in the browser, a
     * [Position]{@link https://developer.mozilla.org/en-US/docs/Web/API/Position} that represents the position
     * of the device at a given time is returned. Latitude and longitude can be accessed from position's
     * [Coordinates]{@link https://developer.mozilla.org/en-US/docs/Web/API/Coordinates}.
     *
     * For every failure, a descriptive error message will be thrown.
     *
     * @param {ContainerContext} context - the context object for the action chain
     * @param {Object} parameters - parameters for the action, corresponding to
     * [PositionOptions]{@link https://developer.mozilla.org/en-US/docs/Web/API/PositionOptions}
     * @param {number} [parameters.maximumAge=0] - The maximum age of the cached position.
     * @param {number} [parameters.timeout=Infinity] - The timeout for the geolocation request in milliseconds.
     * @param {boolean} [parameters.enableHighAccuracy=false] - Whether to use high accuracy for the geolocation request.
     * @param {Object} [options]
     * @param {String} [options.id] - an identifier for the action
     * @returns {Promise<GeolocationPosition>}
     * @throws {Error} If the browser does not support Geolocation API or an error is encountered.
     */
    static async geolocation(context, parameters, options) {
      const opts = Object.assign({}, options, { alias: 'Actions.geolocation' });
      return ActionRunner.run('vb/action/builtin/geolocationAction',
        context, parameters, opts);
    }
    /* eslint-enable max-len */ // If this line comes right after the jsdoc, it is not generated ¯\_(ツ)_/¯

    /**
     * This action method launches the login process for the application.
     * It performs the login action by invoking the handleLogin function on the Security Provider.
     * Redirect to the login page using the login URL given by the security provider configuration.
     * If defined, the returnPath is added to the login URL using the query parameter name defined in the
     * 'returnPathQueryParam' property of the SecurityProvider class.
     *
     * @example
     * {
     *   returnPath: 'https://myapp.com'
     * }
     *
     * @param {ContainerContext} context the context object for the action chain
     * @param {Object} [parameters]
     * @param {String} [parameters.returnPath] used by the login page to return to the application after a
     * successful login
     * @param {Object} [options]
     * @param {String} [options.id] an optional identifier for the action
     * @returns {Promise<boolean>} a promise resolving to true if the login process completed or
     * false if loginUrl is not specified in the SecurityProvider.
     * @throws {Error} if the login process fails.
     */
    static async login(context, parameters, options) {
      const opts = Object.assign({}, options, { alias: 'Actions.login' });
      return ActionRunner.run('vb/action/builtin/loginAction',
        context, parameters, opts);
    }

    /**
     * This action method determines whether there are any dirty data variables in the
     * scope/container from which this action method was invoked. It will check the
     * current container (i.e., Application, Flow, Page, Layout, Fragment, etc.), all
     * its extensions (if present) and any contained containers and their extensions. As
     * long as at least one dirty data variable has been found, this action method will
     * return the dirty state.
     *
     * @param {ContainerContext} context the context object for the action chain
     * @param {Object} [parameters] parameters for the action
     * @param {Object} [options]
     * @param {String} [options.id] an optional identifier for the action
     * @returns {Promise<{status: 'dirty'|'notDirty'}>} a promise resolving to an object with
     * a status property indicating whether there are dirty data variables
     */
    static async getDirtyDataStatus(context, parameters, options) {
      const opts = Object.assign({}, options, { alias: 'Actions.getDirtyDataStatus' });
      return ActionRunner.run('vb/action/builtin/getDirtyDataStatusAction', context, parameters, opts);
    }

    /**
     * This action method re-sets the dirty data state of all tracked variables
     * in the current container's scope and all the contained containers
     * and their extensions.
     *
     * @param {ContainerContext} context the context object for the action chain.
     * @param {Object} [parameters] parameters for the action.
     * @param {Array<String>|String} [parameters.variables] - Optional list of variable expressions to reset.
     * If not provided, resets all variables in the current container's scope and its extensions.
     * @param {Object} [options]
     * @param {String} [options.id] an optional identifier for the action.
     * @returns {Promise<void>} a promise that resolves when the action is complete.
     */
    static async resetDirtyDataStatus(context, parameters, options) {
      const opts = Object.assign({}, options, { alias: 'Actions.resetDirtyDataStatus' });
      return ActionRunner.run('vb/action/builtin/resetDirtyDataStatusAction', context, parameters, opts);
    }

    /**
     * The action method launches the logout process for the application. The following is an example of a
     * parameters object:
     *
     * @example
     * {
     *   logoutUrl: 'someUrl'
     * }
     *
     * @param {ContainerContext} context the context object for the action chain
     * @param {Object} [parameters]
     * @param {String} [parameters.logoutUrl] the URL to navigate to in order to log out
     * @param {Object} [options]
     * @param {String} [options.id] an optional identifier for the action
     * @returns {Promise<void>} throws an error if the logout process is unsuccessful
     */
    static async logout(context, parameters, options) {
      const opts = Object.assign({}, options, { alias: 'Actions.logout' });
      return ActionRunner.run('vb/action/builtin/logoutAction',
        context, parameters, opts);
    }

    /**
     * The action method launches the session lock for the application.
     *
     * @param {Object} context the context object for the action chain
     * @param {Object} [parameters] parameters for the action
     * @param {Object} [options]
     * @param {String} [options.id] an optional identifier for the action
     * @returns {Promise<boolean>} a promise resolving to true if the lock completed or
     * false otherwise.
     * @throws {Error} if the lock fails.
     */
    static async lock(context, parameters, options) {
      const opts = Object.assign({}, options, { alias: 'Actions.lock' });
      return ActionRunner.run('vb/action/builtin/lockAction', context, parameters, opts);
    }

    /**
     * This action method navigates to a page.
     *
     * @example
     * {
     *   page: 'nextPage',
     *   params: {...}
     * }
     *
     * @param {ContainerContext} context - The context object for the action chain.
     * @param {Object} parameters
     * @param {String} parameters.page - The target page.
     * @param {Object} [parameters.params] - A map of URL parameters.
     * @param {'push'|'replace'|'skip'} [parameters.history="push"] - Effect on the browser history. If the value
     * is 'replace', the current browser history entry is replaced, meaning that back button will not go back to it.
     * If the value is 'skip', the URL is left untouched.
     * @param {Object} [options]
     * @param {String} [options.id] - An optional identifier for the action.
     * @returns {Promise<{navigated: boolean}>} a promise resolving when the action is complete. It is possible
     * for the action to complete without navigating, for example when the navigation is vetoed during the
     * vbBeforeEnter event handling. For this reason the promise resolves to an object with a boolean property
     * indicating whether the navigation succeeded.
     * @throws Will throws an error if a problem occured during navigation
     */
    static async navigateToPage(context, parameters, options) {
      const opts = Object.assign({}, options, { alias: 'Actions.navigateToPage' });
      return ActionRunner.run('vb/action/builtin/navigateAction',
        context, parameters, opts);
    }

    /**
     * This action method navigates to a flow.
     *
     * @example
     * {
     *   flow: 'nextFlow',
     *   page: 'nextPage',
     *   params: {...}
     * }
     *
     * @param {ContainerContext} context - The context object for the action chain.
     * @param {Object} parameters
     * @param {String} parameters.flow - The id of the target flow.
     * @param {String} [parameters.page] - The target page. If not specified, navigate to the default page
     * of the flow.
     * @param {'self'|'parent'} [parameters.target="self"] - The target of the destination for flow navigation.
     * This is to change the content of the parent flow instead of the nested flow.
     * @param {Object} [parameters.params] - A map of URL parameters.
     * @param {'push'|'replace'|'skip'} [parameters.history="push"] - Effect on the browser history. If the value
     * is 'replace', the current browser history entry is replaced, meaning that back button will not go back to it.
     * If the value is 'skip', the URL is left untouched.
     * @param {Object} [options]
     * @param {String} [options.id] - An optional identifier for the action.
     * @returns {Promise<{navigated: boolean}>} a promise resolving when the action is complete. It is possible
     * for the action to complete without navigating, for example when the navigation is vetoed during the
     * vbBeforeEnter event handling. For this reason the promise resolves to an object with a boolean property
     * indicating whether the navigation succeeded.
     * @throws Will throws an error if a problem occured during navigation
     */
    static async navigateToFlow(context, parameters, options) {
      const opts = Object.assign({}, options, { alias: 'Actions.navigateToFlow' });
      return ActionRunner.run('vb/action/builtin/navigateAction',
        context, parameters, opts);
    }

    /**
     * This action method navigates to an app UI.
     *
     * @example
     * {
     *   application: 'nextAppUI',
     *   flow: 'nextFlow',
     *   page: 'nextPage',
     *   params: {...}
     * }
     *
     * @param {ContainerContext} context - The context object for the action chain.
     * @param {Object} parameters
     * @param {String} parameters.application - The target app UI.
     * @param {String} [parameters.flow] - The id of target flow. If not specified, navigate to the default flow of
     * the app UI.
     * @param {String} [parameters.page] - The target page. If not specified, navigate to the default page
     * of the flow.
     * @param {Object} [parameters.params] - A map of URL parameters.
     * @param {'push'|'replace'|'skip'} [parameters.history="push"] - Effect on the browser history. If the value
     * is 'replace', the current browser history entry is replaced, meaning that back button will not go back to it.
     * If the value is 'skip', the URL is left untouched.
     * @param {Object} [options]
     * @param {String} [options.id] - An optional identifier for the action.
     * @returns {Promise<{navigated: boolean}>} a promise resolving when the action is complete. It is possible
     * for the action to complete without navigating, for example when the navigation is vetoed during the
     * vbBeforeEnter event handling. For this reason the promise resolves to an object with a boolean property
     * indicating whether the navigation succeeded.
     * @throws Will throws an error if a problem occured during navigation
     */
    static async navigateToApplication(context, parameters, options) {
      const opts = Object.assign({}, options, { alias: 'Actions.navigateToApplication' });
      return ActionRunner.run('vb/action/builtin/navigateAction',
        context, parameters, opts);
    }

    /**
     * The action methods opens the specified resource in the current window or in a new window using the
     * window.open() API as defined at
     * {@link https://developer.mozilla.org/en-US/docs/Web/API/Window/open|Window.open()}.
     *
     * @example
     * {
     *   url: 'http://mysite/index.html',
     *   params: {...}
     *   hash: "details",
     *   windowName: "myOtherWindow",
     * }
     *
     * @param {ContainerContext} context - The context object for the action chain.
     * @param {Object} parameters
     * @param {String} parameters.url - The url to navigate to.
     * @param {Object} [parameters.params] - An object where each property is a URL parameters.
     * @param {String} [parameters.hash] - The hash entry.
     * @param {String} [parameters.windowName] - A name identifying the window as defined in window.open API
     * If not defined, the URL will be open in the current window.
     * @param {'push'|'replace'|'skip'} [parameters.history="push"] - Effect on the browser history. If the value
     * is 'replace', the current browser history entry is replaced, meaning that back button will not go back to it.
     * If the value is 'skip', the URL is left untouched.
     * @param {Object} [options]
     * @param {String} [options.id] - An optional identifier for the action.
     * @returns {Promise<void>}
     * @throws Will throws an error when a parameter is invalid
     */
    static async openUrl(context, parameters, options) {
      const opts = Object.assign({}, options, { alias: 'Actions.openUrl' });
      return ActionRunner.run('vb/action/builtin/openUrlAction',
        context, parameters, opts);
    }

    /**
     * This action method is used to reset a list of variables to their default values. The following is
     * and example of a parameters object:
     * {
     *   variables: ['$page.variables.var1', '$page.variables.var2']
     * }
     *
     * @param {ContainerContext} context the context object for the action chain
     * @param {Object} parameters
     * @param {Array<String>} parameters.variables a list of fully qualified variable names to reset
     * @param {Object} [options]
     * @param {String} [options.id] an optional identifier for the action
     * @returns {Promise<void>} returns a promise that resolves to undefined
     * @throws {Error} throws an error if an error occurs when resetting variables
     */
    static async resetVariables(context, parameters, options) {
      const opts = Object.assign({}, options, { alias: 'Actions.resetVariables' });
      return ActionRunner.run('vb/action/builtin/resetVariablesAction',
        context, parameters, opts);
    }

    /**
     * The action method makes a call against a REST endpoint.
     *
     * The following is an example of a parameters object for a GET REST request:
     *
     * {
     *   endpoint: "employees_70/getEmployeeById",
     *   responseFields: context.$page.variables.fetchedFields,
     *   uriParams: {...}
     *   responseBodyFormat: 'json',
     *   hookHandler: context.$page.variables.configuration.hookHandler,
     *   contentType': 'application/json',
     *   responseType: 'customersResponseType',
     * }
     *
     * The following is an example of a parameters object for a POST REST request:
     *
     * {
     *   endpoint: "fixitfast/postIncident",
     *   uriParams: {...}
     *   body: {...}
     * }
     *
     * @param {ContainerContext} context the context object for the action chain
     * @param {Object} parameters
     * @param {String} parameters.endpoint the endpoint ID as defined in the service configuration
     * @param {Object} [parameters.pathParams] A key/value pair map that will be used to override path
     * parameters as defined in the service endpoint
     * @param {Object} [parameters.queryParams] A key/value pair map that will be used to override query
     * parameters as defined in the service endpoint
     * @param {Object} [parameters.serverVariables] A key/value pair map that will be used to set server variable values
     * used for resolving service connection server URL
     * @param {String} [parameters.body] a structured object that will be sent as the body
     * @param {Object} [parameters.headers] each property name is a header name and value that will be sent
     * with the request
     * @param {String} [parameters.contentType] optional string value with an actual (MIME) type, which will
     * be used for the "content-type" header. When used with "fileContentType", this is also used as the
     * type for the File blob.
     * @param {String} [parameters.responseType] if a responseType is set, coerce the REST response body to this type
     * @param {String} [parameters.filePath] optional path to a file to send with the request
     * @param {String} [parameters.filePartName] optional, used with filePath; allow override of the default name
     * ("file") for the FormData part
     * @param {String} [parameters.fileContentType] optional string, used in combination with "contentType":
     * "multipart/form-data" and "filePath"
     * @param {Function} [parameters.hookHandler] used primarily by vb/ServiceDataProvider when externalizing
     * data fetches
     * @param {Object} [parameters.requestTransformOptions] a map of keys (transform function names) and values
     * to pass to the corresponding transform, as the "options" parameter
     * @param {Object} [parameters.requestTransformFunctions] A map of named transform functions, called before
     * making the request, where the function is: fn(configuration, options)
     * @param {Object} [parameters.responseTransformFunctions] A map of named transform functions, called before
     * making the response, where the function is: fn(configuration, options)
     * @param {String} [parameters.responseBodyFormat] 'json' or 'formData'
     * @param {Array} [parameters.responseFields] advanced field for use specifically with JET Dynamic Forms.
     * The value would typically be a variable that is bound to the <oj-dynamic-form> "rendered-fields"
     * attribute. This is how a calculated layout can the Rest Action call which fields to fetch.
     * @param {Object} [parameters.uriParams] A key/value pair map that will be used to override path and query
     * parameters as defined in the service endpoint. (Note: This parameter is deprecated in favor of
     * {@link serverVariables}, {@link pathParams} and {@link queryParams}.)
     * @param {Object} [options]
     * @param {String} [options.id] an optional identifier for the action
     * @returns {Promise<{body: any, headers: Headers, ok: boolean, status: number, statusText: string}>} returns the
     * response object from the browser fetch and let the JS chain handle the response status. Only errors from the
     * browser fetch will be thrown from this method.
     */
    static async callRest(context, parameters, options) {
      const opts = Object.assign({}, options, { alias: 'Actions.callRest' });
      return ActionRunner.run('vb/action/builtin/restAction',
        context, parameters, opts);
    }

    /**
     * Navigate back to the previous page in browser history with input parameters.
     * It executes the back() method of the browser history except when in a oj-vb-switcher component.
     *
     * In a switcher component, the browser back() method can switch back to the previous tab. This behavior
     * is not always desired, so the navigateBackAction ‘behaves’ differently, by going back to the previous
     * page in that tab.
     *
     * @param {ContainerContext} context - The context object for the action chain.
     * @param {Object} [parameters]
     * @param {Object} [parameters.params] - The input parameters for the page
     * the action is going back to.
     * @param {Object} [options]
     * @param {String} [options.id] - An optional identifier for the action.
     * @returns {Promise<void>} a promise resolving when the action is complete.
     * @throws Will throws an error if an error occured.
     */
    static async navigateBack(context, parameters, options) {
      const opts = Object.assign({}, options, { alias: 'Actions.navigateBack' });
      return ActionRunner.run('vb/action/builtin/navigateBackAction',
        context, parameters, opts);
    }

    /**
     * This action method allows applications to invoke the native sharing capabilities of the host
     * platform and share content with other applications, such as Facebook, Twitter, Slack, SMS, etc.
     * This action should only be invoked following a user gesture (such as a button click).
     * It is a good idea to only enable share UI based off feature detection:
     * <pre>
     *   <oj-button disabled="[[!navigator.share]]">Share</oj-button>
     * </pre>
     *
     * The parameters object correspond to
     * [Web Share API]{@link https://developer.mozilla.org/en-US/docs/Web/API/Navigator/share}
     * All parameters are individually optional, but at least one parameter has to be specified. Any url
     * can be shared, not just urls under website's current scope. Text can be shared with or without a url.
     *
     * The following is an example of a parameters object:
     *
     * {
     *   title: document.querySelector('h1').textContent,
     *   url: document.querySelector('link[rel=canonical]') &&
     *        document.querySelector('link[rel=canonical]').href || window.location.href,
     *   files: context.$page.variables.files
     * }
     *
     * A success outcome is returned once user has completed a share action. A failure outcome is returned
     * when browser does not support Web Share API or a parameter error is detected.
     * @param {ContainerContext} context - the context object for the action chain
     * @param {Object} parameters - parameters for the action
     * @param {string} [parameters.title] - The title of the document being shared. May be ignored by the
     * handler/target.
     * @param {string} [parameters.text] - Arbitrary text that forms the body of the message being shared.
     * @param {string} [parameters.url] - The URL of the resource being shared.
     * @param {File[]} [parameters.files] - The list of files to be shared, such as image, video, audio, and
     * text files.
     * @param {Object} [options]
     * @param {String} [options.id] - an identifier for the action
     * @returns {Promise<void>}
     * @throws {Error} if the browser does not support Web Share API or a parameter error is detected.
     */
    static async webShare(context, parameters, options) {
      const opts = Object.assign({}, options, { alias: 'Actions.webShare' });
      return ActionRunner.run('vb/action/builtin/webShareAction',
        context, parameters, opts);
    }

    /**
     * Sends a WebSocket message.
     *
     * @param {ContainerContext} context - The context object for the action chain.
     * @param {Object} parameters - Parameters for the action.
     * @param {WebSocket} parameters.webSocket - The WebSocket instance to receive the message.
     * @param {String} parameters.message - The message to be sent.
     * @param {Object} [options] - Options for the action.
     * @param {String} [options.id] - An optional identifier for the action.
     * @returns {Promise<void>} Returns a promise that resolves when the message is sent.
     * @throws {Error} Throws an error if an error occurs when sending the message.
     */
    static async sendWebSocketMessage(context, parameters, options) {
      const opts = Object.assign({}, options, { alias: 'Actions.sendWebSocketMessage' });
      return ActionRunner.run('vb/action/builtin/sendWebSocketMessageAction',
        context, parameters, opts);
    }

    /**
    * Exports the layout from a given metadata descriptor. It returns an object containing a Blob
    * whose content is a serialized representation of the layout along with a hash that is used to
    * verify the content of the Blob.
    *
    * @param {Object} context - The context object for the action chain.
    * @param {Object} parameters - Parameters for the action.
    * @param {Object} parameters.metadataDescriptor - The metadata descriptor from which to export the layout.
    * It should be an instance of vb/DynamicLayoutMetadataProviderDescriptor or
    * vb/HeterogeneousMetadataProviderDescriptor
    * @param {Object} [options] - Options for the action.
    * @param {String} [options.id] - An optional identifier for the action.
    * @returns {Promise<{hash: number, blob: Blob}>} Returns a promise that resolves when the layout is exported.
    * @throws {Error} Throws an error if an error occurs when exporting the layout.
    */
    static async exportLayout(context, parameters, options) {
      const opts = Object.assign({}, options, { alias: 'Actions.exportLayout' });
      return ActionRunner.run('vb/action/builtin/exportLayoutAction',
        context, parameters, opts);
    }

    /**
     * Loads a layout resource out-of-band.
     *
     * @param {Object} context - The context object for the action chain.
     * @param {Object} parameters - Parameters for the action.
     * @param {Object} parameters.metadataDescriptor - The metadata descriptor from which to export the layout. It
     * should be an instance of vb/DynamicLayoutMetadataProviderDescriptor.
     * @param {string} parameters.fileName - The file name of the resource to load. Only '.json' and '.html' file
     * extensions are supported. In the case of a JSON file, a JSON object will be returned. In the case of a HTML
     * file, a string will be returned.
     * @param {Object} [options] - Options for the action.
     * @param {String} [options.id] - An optional identifier for the action.
     * @returns {Promise<*>} Returns a promise that resolves when the layout resource is loaded.
     * @throws {Error} Throws an error if an error occurs when loading the layout resource.
     */
    static async loadLayoutResource(context, parameters, options) {
      const opts = Object.assign({}, options, { alias: 'Actions.loadLayoutResource' });
      return ActionRunner.run('vb/action/builtin/loadLayoutResourceAction',
        context, parameters, opts);
    }

    /**
     * This action method can be used to invoke any custom action.
     *
     * @param {ContainerContext} context - the context object for the action chain
     * @param {Object} parameters
     * @param {String} parameters.module - module id for the custom action
     * @param {Object} parameters.parameters - parameters to the custom action
     * @param {Object} [options]
     * @param {String} [options.id] - an optional identifier for the action
     * @returns {Promise<*>} returns a promise that resolves to the result of a custom action
     * @throws {Error} throws an error if the custom action fails
     */
    static async runAction(context, parameters, options) {
      return ActionRunner.run(parameters.module,
        context, parameters.parameters, options);
    }
  }

  return Actions;
});

